import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { ActiveAccountService } from './active-account.service';


// =============================================================================
// IMPORTANT - Must wait at least 1.5 seconds before you attempt to get the
// refreshed token after calling RefreshToken()
// 
// Check for undefined and try again.
// =============================================================================


@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {

  private tokenSrouce$ = new BehaviorSubject<string | null>(null)
  token$ = this.tokenSrouce$.asObservable();
  
  get token() {
    return this.tokenSrouce$.getValue();
  }
  // ==========================================
  //
  // ==========================================
  constructor(
    // ONE MSAL instance, not two. This service used to `new
    // PublicClientApplication(msalConfig)` on every RefreshToken() call, which
    // gave it a second, independent view of the same localStorage cache,
    // separate from the one MsalModule/MsalInterceptor use. Two views of one
    // cache can disagree about which account is current, and the interceptor's
    // token and this service's token could then be issued for DIFFERENT users
    // inside a single session. Injecting the shared MsalService means there is
    // exactly one answer.
    private msal: MsalService,
    private activeAccount: ActiveAccountService,
  ) {

    // --------------------------------------------------------------------
    // NOTE: This Service is setup and called by the APP_INITIALIZER
    // --------------------------------------------------------------------
    console.log("AuthTokenService -- Constructor");

  }


  // =========================================================
  // Called automatically from Constructor
  // Only re-call if token expired.
  //
  // NEED TO TEST to see if token expired or auto-renews
  // =========================================================

  async RefreshToken() {

    console.log("AuthTokenService -- RefreshToken -- entered");



    console.log("AuthTokenService --> RefreshToken()");

    let publicClientApplication = this.msal.instance;

    // WAS: publicClientApplication.getAllAccounts()[0].
    //
    // Index zero is localStorage key order, not identity. On a machine with two
    // cached B2C accounts it silently selected whichever the cache happened to
    // enumerate first, and MSAL reorders those keys when it rewrites entries on
    // token refresh. Because RefreshToken() also runs on every network recovery
    // (auth.service.ts initialize()), a long-lived session could change identity
    // mid-flight with no sign-in and no sign-out. See ActiveAccountService.
    const account = this.activeAccount.current();

    console.log("AuthTokenService --> Account resolved (present:", !!account, ")");

    // Several accounts and none pinned. There is no honest answer here, so do
    // NOT fall back to an arbitrary one: ActiveAccountService is already
    // prompting for the account picker, and a token minted for a guessed
    // identity is worse than no token.
    if (!account && this.activeAccount.isAmbiguous()) {
      console.warn("AuthTokenService --> account is ambiguous, awaiting account selection");
      return Promise.resolve(false);
    }

    // -------------------------------------------------------------------------------------
    // Return Promise
    // The AUTH Token MUST be acquired before this method returns to the calling page
    // -------------------------------------------------------------------------------------
    return new Promise((resolve, reject) => {

      if (!account) {
        // -------------------------------------------------------------------------------------------------
        // User Just Logged In
        // After a User Logs in, the getAllAccounts() fails to obtain the newly logged in Account.
        // We MUST wait at least 1 second before attempting to get it again.
        // Once user is logged in, any page refreshes do not have this issue.
        // -------------------------------------------------------------------------------------------------
        console.error("AuthTokenService --> Account Not Set... Trying again in 1 second...");

        setTimeout(() => {
          console.log("AuthTokenService --> Account Not Set... Trying to acquire again...");
          // Same correction as above: resolved identity, never index zero. By
          // this point a just-completed sign-in has raised LOGIN_SUCCESS, so
          // ActiveAccountService has pinned the account the user actually
          // authenticated as.
          const account = this.activeAccount.current();
          console.log("AuthTokenService --> Account 2 resolved (present:", !!account, ")");

          if (!account && this.activeAccount.isAmbiguous()) {
            // Do not redirect to a token request for an unknown identity; the
            // account picker is already in flight.
            console.warn("AuthTokenService --> still ambiguous, awaiting account selection");
            return resolve(false);
          }

          if(account == undefined){
            // ------------------------------------
            // Account not found...
            // User needs to login
            // ------------------------------------
            const accessTokenRequest = {
              scopes: [environment.scopes],
            }
      
            publicClientApplication.acquireTokenRedirect(accessTokenRequest);  
          }

          return resolve(this.AcquireToken(account, publicClientApplication));

        }, 2000); // MINIMUM 1000, otherwise, it still fails.

      }
      else {
        console.log("AuthTokenService --> Account Ready on 1st Instance");

        return resolve(this.AcquireToken(account, publicClientApplication));
      }

    });

  }


  // ================================================================================
  // Acquire Token
  // Make sure to return the Promise
  // ================================================================================
  AcquireToken(account, publicClientApplication) {

    return new Promise((resolve, reject) => {

      const accessTokenRequest = {
        scopes: [environment.scopes],
        account: account
      }

      // var _this = this;

      console.log("AuthTokenService --> Attempting to AcquireTokenSilent");

      publicClientApplication.acquireTokenSilent(accessTokenRequest).then(re => {

        console.log("AuthTokenService --> AcquireTokenSilent SUCCESS");

        let refreshedToken = re.accessToken;

        this.tokenSrouce$.next(refreshedToken);
        console.log("AuthTokenService --> token refreshed (present:", !!this.token, ")");

        resolve(true);

      })
      .catch(function (error) 
      {
        if (error instanceof InteractionRequiredAuthError) {
          console.error("AuthTokenService --> RefreshToken - Redirecting user to login...", error);
          publicClientApplication.acquireTokenRedirect(accessTokenRequest);
        }
        else {
          console.error("AuthTokenService --> RefreshToken - Login Error #1", error);
          reject(error);
        }
      });
    });

  }


  // ================================================================================
  //
  // ================================================================================
  isTokenExpired() {
    // No token is not the same as a valid one, and it must not throw. This used
    // to TypeError on `this.token.split` whenever the token was null, which
    // callers (lock-screen, advanced, editnotepopup) invoke directly. That state
    // was transient before; refusing to mint a token for an ambiguous identity
    // makes it reachable and durable, so treat "absent" as "needs refreshing".
    if (!this.token) { return true; }
    try {
      const expiry = (JSON.parse(atob(this.token.split('.')[1]))).exp;
      return (Math.floor((new Date).getTime() / 1000)) >= expiry;
    } catch {
      // Unparseable is not usable.
      return true;
    }
  }


  // ================================================================================
  //
  // ================================================================================
  async refreshAuthTokenIfExpired(){
    if(!this.token || this.isTokenExpired()){
      // --------------------------------
      // Token Expired - Refresh
      // --------------------------------
      await this.RefreshToken();
      console.log("Auth Token Expired - RefreshToken Called");
    }
  }



  // ================================================================================
  //
  // ================================================================================
  getTryAgainRefreshTokenMsg() {
    return "Refreshing Login Token<br>Please try again in a few seconds.<br>If you continue to see this message, please do a page refresh.";
  }


}